import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ApiService } from '../../services';
import { buildLoginPathWithRedirect } from '../../utils/auth-redirect.util';
import { useSelected } from './use-selected';
import { useSetSelected } from './use-set-selected';

export const useSelectCompanyFromQueryParam = (enabled: boolean) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const companyIdFromUrl = searchParams.get('companyId');
  const { selectedCompanyId, isLoading } = useSelected();
  const { setSelected, isPending } = useSetSelected();
  const [isHandled, setIsHandled] = useState(!companyIdFromUrl);
  const didAttemptSelectRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (!companyIdFromUrl) {
      setIsHandled(true);
      return;
    }

    if (isLoading || isPending) {
      return;
    }

    if (selectedCompanyId === companyIdFromUrl) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('companyId');
      setSearchParams(nextParams, { replace: true });
      setIsHandled(true);
      return;
    }

    if (didAttemptSelectRef.current) {
      return;
    }

    didAttemptSelectRef.current = true;

    setSelected(companyIdFromUrl, {
      onSuccess: () => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('companyId');
        setSearchParams(nextParams, { replace: true });
        setIsHandled(true);
      },
      onError: async () => {
        const redirectPath = `${window.location.pathname}${window.location.search}`;
        await ApiService.clearStaleAuthSession();
        navigate(buildLoginPathWithRedirect(redirectPath), { replace: true });
      },
    });
  }, [
    companyIdFromUrl,
    enabled,
    isLoading,
    isPending,
    navigate,
    searchParams,
    selectedCompanyId,
    setSearchParams,
    setSelected,
  ]);

  return { isSelectingCompany: enabled && !!companyIdFromUrl && !isHandled };
};
